import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Bike, CheckCircle2, ClipboardList, CreditCard, PackageCheck, Truck, X } from 'lucide-react';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import OrderDetailsDrawer from '../../components/OrderDetailsDrawer';
import AssignRiderDrawer from '../../components/AssignRiderDrawer';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  ORDER_QUEUE_COLUMNS,
  ORDER_QUEUE_ERRORS,
  ORDER_STATUS,
  assignRider as assignRiderApi,
  cancelOrder as cancelOrderApi,
  confirmOrder as confirmOrderApi,
  getOrdersSummary,
  listOrders,
  listRiders,
  packOrder as packOrderApi,
  // resendBill as resendBillApi,
  sortByUrgency,
  queueColumnsForEvents,
  unassignRider as unassignRiderApi,
} from '../../api/orders';
import { statusColors } from '../../theme';
import useStoreQueueLive from '../../hooks/useStoreQueueLive';
import OrderCard from './OrderCard';
import styles from './OrderQueue.module.css';

const MAX_TRIP_ORDERS = 2;

const WORKING_COLUMNS = [
  ORDER_STATUS.RECEIVED,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.PACKED,
  ORDER_STATUS.OUT_FOR_DELIVERY,
];

const HISTORY_COLUMNS = [
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.FAILED_DELIVERY,
];

let rememberedBoard = null;
let rememberedSummary = null;

const STAT_SKELETONS = [
  { key: 'total', tint: '#F2B922' },
  { key: 'awaiting', tint: '#2F6FED' },
  { key: 'unpaid', tint: '#B45309' },
  { key: 'transit', tint: '#0D9488' },
  { key: 'delivered', tint: '#2E7D32' },
];

function friendlyError(err) {
  return ORDER_QUEUE_ERRORS[err.code]
    || ORDER_QUEUE_ERRORS[err.message]
    || err.detail
    || err.message
    || 'Something went wrong.';
}

function decorateColumn(list, ridersMap) {
  return sortByUrgency(list).map((order) => ({
    ...order,
    rider: order.deliveryPersonSub
      ? (ridersMap[order.deliveryPersonSub] || {
        id: order.deliveryPersonSub,
        name: order.deliveryPersonSub,
        phone: null,
      })
      : null,
  }));
}

export default function OrderQueue() {
  const { user } = useAuth();
  const { notify, noteQueueSummary } = useOrders();
  const [ordersByStatus, setOrdersByStatus] = useState(() => rememberedBoard?.ordersByStatus || {});
  const [riders, setRiders] = useState(() => rememberedBoard?.riders || []);
  const [summary, setSummary] = useState(() => rememberedSummary);
  const [loadedKeys, setLoadedKeys] = useState(() => new Set(
    rememberedBoard?.ordersByStatus ? Object.keys(rememberedBoard.ordersByStatus) : [],
  ));
  const [historyLoaded, setHistoryLoaded] = useState(() => Boolean(rememberedBoard?.historyLoaded));
  const [error, setError] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);
  const [assignTargets, setAssignTargets] = useState([]);
  const [selectedPacked, setSelectedPacked] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const loadSeq = useRef(0);
  const boardReady = useRef(false);
  const queuedEvents = useRef(null);
  const ridersRef = useRef(riders);
  ridersRef.current = riders;

  const load = useCallback(async ({ silent = false, statuses = null } = {}) => {
    const gen = ++loadSeq.current;
    const partial = Array.isArray(statuses) && statuses.length > 0;
    const columnKeys = partial ? statuses : WORKING_COLUMNS;
    if (!silent) setError('');
    if (!partial) {
      getOrdersSummary(user.idToken).then(
        (data) => {
          if (gen !== loadSeq.current) return;
          rememberedSummary = data;
          setSummary(data);
          noteQueueSummary(data);
        },
        (error) => {
          if (gen !== loadSeq.current || rememberedSummary) return;
          notify(friendlyError(error));
        },
      );
    }

    const ridersPromise = partial
      ? null
      : listRiders(user.idToken).then(
        (list) => ({ list, error: null }),
        (error) => ({ list: null, error }),
      );

    let firstError = null;
    let failures = 0;
    await Promise.all(columnKeys.map(async (key) => {
      try {
        const list = await listOrders(key, user.idToken);
        if (gen !== loadSeq.current) return;
        const ridersMap = Object.fromEntries(ridersRef.current.map((rider) => [rider.id, rider]));
        const decorated = decorateColumn(list, ridersMap);
        setOrdersByStatus((prev) => {
          const next = { ...prev, [key]: decorated };
          rememberedBoard = {
            ordersByStatus: next,
            riders: ridersRef.current,
            historyLoaded: rememberedBoard?.historyLoaded || false,
          };
          return next;
        });
        setLoadedKeys((prev) => {
          if (prev.has(key)) return prev;
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        if (key === ORDER_STATUS.PACKED) {
          const packedIds = new Set(decorated.map((order) => order.id));
          setSelectedPacked((sel) => sel.filter((id) => packedIds.has(id)));
        }
      } catch (err) {
        failures += 1;
        if (!firstError) firstError = err;
      }
    }));

    if (gen !== loadSeq.current) return;

    if (ridersPromise) {
      const ridersResult = await ridersPromise;
      if (gen !== loadSeq.current) return;
      if (ridersResult.list) {
        ridersRef.current = ridersResult.list;
        setRiders(ridersResult.list);
        const ridersMap = Object.fromEntries(ridersResult.list.map((rider) => [rider.id, rider]));
        setOrdersByStatus((prev) => {
          const next = { ...prev };
          columnKeys.forEach((key) => {
            if (next[key]) next[key] = decorateColumn(next[key], ridersMap);
          });
          rememberedBoard = {
            ordersByStatus: next,
            riders: ridersResult.list,
            historyLoaded: rememberedBoard?.historyLoaded || false,
          };
          return next;
        });
      } else if (ridersResult.error) {
        notify(friendlyError(ridersResult.error));
      }
    }

    if (failures === columnKeys.length && firstError) {
      if (silent || rememberedBoard) notify(friendlyError(firstError));
      else setError(firstError.message || 'Could not load the order queue.');
      return;
    }

    boardReady.current = true;

    const queued = queuedEvents.current;
    if (queued?.length && gen === loadSeq.current) {
      queuedEvents.current = null;
      load({ silent: true, statuses: queueColumnsForEvents(queued) });
      return;
    }

    if (partial || gen !== loadSeq.current) return;

    try {
      const historyColumns = await Promise.all(
        HISTORY_COLUMNS.map((key) => listOrders(key, user.idToken)),
      );
      if (gen !== loadSeq.current) return;
      const ridersMap = Object.fromEntries(ridersRef.current.map((rider) => [rider.id, rider]));
      const decoratedHistory = historyColumns.map((list) => decorateColumn(list, ridersMap));
      setOrdersByStatus((prev) => {
        const next = { ...prev };
        HISTORY_COLUMNS.forEach((key, index) => { next[key] = decoratedHistory[index]; });
        rememberedBoard = {
          ordersByStatus: next,
          riders: ridersRef.current,
          historyLoaded: true,
        };
        return next;
      });
      setHistoryLoaded(true);
    } catch (err) {
      if (gen !== loadSeq.current) return;
      setHistoryLoaded(true);
      notify(friendlyError(err));
    }
  }, [user.idToken, notify, noteQueueSummary]);

  useEffect(() => {
    load({ silent: Boolean(rememberedBoard) });
  }, [load]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  const refreshFromLive = useCallback((events) => {
    if (!boardReady.current) {
      queuedEvents.current = (queuedEvents.current || []).concat(events || []);
      return;
    }
    load({ silent: true, statuses: queueColumnsForEvents(events) });
  }, [load]);

  useStoreQueueLive(refreshFromLive);

  const riderTripCounts = useMemo(() => {
    const counts = {};
    (ordersByStatus[ORDER_STATUS.OUT_FOR_DELIVERY] || []).forEach((order) => {
      if (!order.rider) return;
      counts[order.rider.id] = (counts[order.rider.id] || 0) + 1;
    });
    return counts;
  }, [ordersByStatus]);

  const toggleSelectPacked = useCallback((orderId) => {
    setSelectedPacked((prev) => {
      if (prev.includes(orderId)) return prev.filter((id) => id !== orderId);
      if (prev.length >= MAX_TRIP_ORDERS) return prev;
      return [...prev, orderId];
    });
  }, []);

  const openAssignOne = useCallback((order) => {
    setAssignTargets([order]);
  }, []);

  const openBatchAssign = () => {
    const targets = (ordersByStatus[ORDER_STATUS.PACKED] || []).filter((o) => selectedPacked.includes(o.id));
    setAssignTargets(targets);
  };

  const closeAssignDrawer = () => {
    setAssignTargets([]);
    setSelectedPacked([]);
  };

  const withBusy = useCallback(async (id, action, successMessage) => {
    setBusyId(id);
    try {
      await action();
      if (successMessage) notify(successMessage);
      await refresh();
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  }, [notify, refresh]);

  const handleConfirm = useCallback(
    (id) => withBusy(id, () => confirmOrderApi(id, user.idToken), `Order ${id} confirmed`),
    [withBusy, user.idToken],
  );
  const handleCancel = useCallback(
    (id) => withBusy(id, () => cancelOrderApi(id, undefined, user.idToken), `Order ${id} cancelled`),
    [withBusy, user.idToken],
  );
  const handlePack = useCallback(
    (id) => withBusy(id, () => packOrderApi(id, user.idToken), `Order ${id} marked packed`),
    [withBusy, user.idToken],
  );
  const handleUnassign = useCallback(
    (id) => withBusy(id, () => unassignRiderApi(id, user.idToken), `Rider unassigned from ${id}`),
    [withBusy, user.idToken],
  );

  const handleAssign = async (orderIds, riderId) => {
    const rider = riders.find((r) => r.id === riderId);
    setBusyId(orderIds[0]);
    try {
      await Promise.all(orderIds.map((id) => assignRiderApi(id, riderId, user.idToken)));
      notify(
        orderIds.length > 1
          ? `${rider?.name || 'Rider'} assigned to ${orderIds.length} orders (${orderIds.join(', ')})`
          : `${rider?.name || 'Rider'} assigned to ${orderIds[0]}`,
      );
      await refresh();
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    if (!summary) return [];
    const total = Object.values(summary.counts).reduce((sum, n) => sum + n, 0);
    return [
      { key: 'total', icon: ClipboardList, label: 'Total orders', value: total, tint: '#F2B922' },
      { key: 'awaiting', icon: PackageCheck, label: 'Awaiting confirmation', value: summary.awaitingConfirmation, tint: '#2F6FED' },
      { key: 'unpaid', icon: CreditCard, label: 'Awaiting payment', value: summary.awaitingPayment, tint: '#B45309' },
      { key: 'transit', icon: Truck, label: 'Out for delivery', value: summary.outForDelivery, tint: '#0D9488' },
      { key: 'delivered', icon: CheckCircle2, label: 'Delivered', value: summary.delivered, tint: '#2E7D32' },
    ];
  }, [summary]);

  return (
    <div>
      {summary ? (
        <div className={styles.statGrid}>
          {stats.map((s) => (
            <StatCard key={s.key} icon={s.icon} label={s.label} value={s.value} tint={s.tint} />
          ))}
        </div>
      ) : (
        <div className={styles.statGrid} aria-busy="true" aria-label="Loading order totals">
          {STAT_SKELETONS.map((item) => (
            <div key={item.key} className={styles.statSkeleton} style={{ borderLeftColor: item.tint }}>
              <span className={styles.statSkeletonIcon} />
              <span className={styles.statSkeletonBody}>
                <span className={styles.statSkeletonValue} />
                <span className={styles.statSkeletonLabel} />
              </span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load the order queue"
          description={error}
          action={<Button variant="outline" onClick={() => load()}>Retry</Button>}
        />
      )}

      {!error && (
        <div className={styles.board}>
          {ORDER_QUEUE_COLUMNS.map((col) => {
            const columnOrders = ordersByStatus[col.key] || [];
            const accent = statusColors[col.key]?.fg;
            const isPackedColumn = col.key === ORDER_STATUS.PACKED;
            return (
              <section className={styles.column} key={col.key}>
                <header className={styles.columnHeader}>
                  <div className={styles.columnTitleRow}>
                    <span className={styles.columnDot} style={{ background: accent }} />
                    <h3 className={styles.columnTitle}>{col.title}</h3>
                    <span className={styles.columnCount}>{columnOrders.length}</span>
                  </div>
                  <p className={styles.columnHint}>{col.hint}</p>
                </header>

                {isPackedColumn && selectedPacked.length > 0 && (
                  <div className={styles.batchBar}>
                    <span className={styles.batchCount}>
                      {selectedPacked.length} of {MAX_TRIP_ORDERS} selected for one trip
                    </span>
                    <div className={styles.batchActions}>
                      <button className={styles.batchClear} onClick={() => setSelectedPacked([])} aria-label="Clear selection">
                        <X size={13} />
                      </button>
                      <Button size="sm" variant="primary" icon={Bike} onClick={openBatchAssign}>
                        Assign together
                      </Button>
                    </div>
                  </div>
                )}

                <div className={styles.columnBody}>
                  {(HISTORY_COLUMNS.includes(col.key) ? !historyLoaded : !loadedKeys.has(col.key)) ? (
                    <p className={styles.loadingText}>Loading…</p>
                  ) : columnOrders.length === 0 ? (
                    <EmptyState title="All clear" description="No orders here right now." />
                  ) : (
                    columnOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onOpen={setActiveOrder}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                        onPack={handlePack}
                        onAssign={openAssignOne}
                        onUnassign={handleUnassign}
                        tripSize={order.rider ? riderTripCounts[order.rider.id] : 0}
                        selected={isPackedColumn ? selectedPacked.includes(order.id) : undefined}
                        selectDisabled={isPackedColumn ? selectedPacked.length >= MAX_TRIP_ORDERS : undefined}
                        onToggleSelect={isPackedColumn ? toggleSelectPacked : undefined}
                        busy={busyId === order.id}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Resend invoice is intentionally disabled in the Order Queue. */}
      <OrderDetailsDrawer
        order={activeOrder}
        onClose={() => setActiveOrder(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onPack={handlePack}
        onAssign={(order) => { setActiveOrder(null); setAssignTargets([order]); }}
        onUnassign={handleUnassign}
      />

      <AssignRiderDrawer
        orders={assignTargets}
        riders={riders}
        activeRideCounts={riderTripCounts}
        onClose={closeAssignDrawer}
        onAssign={handleAssign}
      />
    </div>
  );
}
